import type { AuthUserPayload } from "@/lib/api/types";
import type { ConfigDefaults, PostHog, Properties } from "posthog-js";

type PostHogClientConfig = {
  apiKey: string;
  apiHost: string;
  defaults: ConfigDefaults;
};

type ClientConfigResponse = {
  status: "success" | "error";
  data?: {
    posthog?: PostHogClientConfig | null;
  };
};

const POSTHOG_SOURCE_APP = "shippeek_tms";

let configPromise: Promise<PostHogClientConfig | null> | null = null;
let posthogPromise: Promise<PostHog | null> | null = null;
let clickTrackingInstalled = false;

async function getPostHogConfig(): Promise<PostHogClientConfig | null> {
  if (typeof window === "undefined") return null;

  configPromise ??= fetch("/api/client-config", { credentials: "same-origin" })
    .then(async (response) => {
      if (!response.ok) return null;
      const body = (await response.json()) as ClientConfigResponse;
      return body.data?.posthog ?? null;
    })
    .catch(() => null);

  return configPromise;
}

async function getPostHog(): Promise<PostHog | null> {
  if (typeof window === "undefined") return null;

  posthogPromise ??= getPostHogConfig()
    .then(async (config) => {
      if (!config?.apiKey) return null;

      const { default: posthog } = await import("posthog-js");

      if (!posthog.__loaded) {
        posthog.init(config.apiKey, {
          api_host: config.apiHost,
          defaults: config.defaults,
          capture_pageview: false,
          loaded: (client) => {
            client.register({ source_app: POSTHOG_SOURCE_APP });
          },
        });
      }

      posthog.register({ source_app: POSTHOG_SOURCE_APP });
      return posthog;
    })
    .catch((error) => {
      posthogPromise = null;
      console.warn("PostHog failed to initialize", error);
      return null;
    });

  return posthogPromise;
}

export function initPostHog(): void {
  void getPostHog();
}

export async function capturePostHogEvent(eventName: string, properties: Properties = {}): Promise<void> {
  const posthog = await getPostHog();
  posthog?.capture(eventName, {
    source_app: POSTHOG_SOURCE_APP,
    ...properties,
  });
}

export async function capturePostHogPageview(): Promise<void> {
  if (typeof window === "undefined") return;

  await capturePostHogEvent("$pageview", {
    $current_url: window.location.href,
    source_path: window.location.pathname,
  });
}

export async function identifyPostHogUser(user: AuthUserPayload): Promise<void> {
  const posthog = await getPostHog();
  posthog?.identify(user.id, {
    email: user.email,
    name: user.name ?? undefined,
    email_verified: user.emailVerified,
    two_factor_enabled: user.twoFactorEnabled,
    source_app: POSTHOG_SOURCE_APP,
  });
}

export async function resetPostHog(): Promise<void> {
  const posthog = await getPostHog();
  posthog?.reset();
}

export function installPostHogClickTracking(): () => void {
  if (typeof document === "undefined" || clickTrackingInstalled) {
    return () => {};
  }

  const onClick = (event: MouseEvent) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const element = target.closest<HTMLElement>("[data-posthog-event]");
    if (!element) return;

    const eventName = element.dataset.posthogEvent;
    if (!eventName) return;

    const anchor = element instanceof HTMLAnchorElement ? element : element.closest("a");
    const isModifiedClick =
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey;
    const opensNewTab = anchor?.target === "_blank";
    const shouldDelayNavigation = Boolean(anchor?.href && !isModifiedClick && !opensNewTab);

    if (shouldDelayNavigation) {
      event.preventDefault();
    }

    void capturePostHogEvent(eventName, {
      cta_label: element.dataset.posthogLabel || (element.textContent || "").trim(),
      cta_location: element.dataset.posthogLocation || "unknown",
      cta_destination: anchor?.href,
      source_path: window.location.pathname,
    });

    if (shouldDelayNavigation && anchor?.href) {
      window.setTimeout(() => {
        window.location.assign(anchor.href);
      }, 150);
    }
  };

  document.addEventListener("click", onClick);
  clickTrackingInstalled = true;

  return () => {
    document.removeEventListener("click", onClick);
    clickTrackingInstalled = false;
  };
}
